import "./style.scss";

import AddCircleOutline from "@mui/icons-material/AddCircleOutline";
import { Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import ModalBox from "components/Modal";
import ButtonBase from "components/shared/ButtonBase";
import DeleteModal from "components/shared/DeleteModal";
import { Spinner } from "components/shared/Spinner";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "store/store";
import { deleteAnalyticalCode, fetchAnalyticalCodes } from "store/titleStore";
import type { IAnalyticalCodes } from "store/types";

import CodeForm from "./Modals/CodeForm";
import DeleteCode from "./Modals/DeleteCode";

const buttonStyle = {
    fontSize: "12px",
    padding: "6px 16px",
    borderRadius: "3px",
    border: "1px solid #EAEAEA",
    color: "#FFFFFF",
};

const tHeadStyles = {
    maxWidth: 200,
    fontWeight: 600,
    fontSize: "12px",
    width: "min-content",
    padding: "10px 12px",
    // backgroundColor: "#F8F8F8",
    fontFamily: "'Poppins', sans-serif",
    color: "#676767",
    // borderBottom: "none",
};

interface CodeListColumns {
    id: number;
    label: string | null;
    name: string;
    align: "left" | "center" | "right" | "inherit" | "justify" | undefined;
    format: null | ((data: any) => string);
}

const AnalyticalCode = () => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const { codes, codesStatus } = useAppSelector((state) => state.titles);
    const company = useAppSelector((state) => state.settings.companySettings);
    const [isEdit, setIsEdit] = useState(false);
    const [currentCode, setCurrentCode] = useState<IAnalyticalCodes | undefined>();

    useEffect(() => {
        dispatch(fetchAnalyticalCodes());
    }, [dispatch]);

    // In case we add headers, for now I don't think they're useful
    const codeListColumns: CodeListColumns[] = [
        {
            id: 0,
            label: "AnalyticalCode.Code",
            name: "name",
            align: "left",
            format: null,
        },
    ];

    const handleClickEdit = (code: IAnalyticalCodes) => {
        setIsEdit(true);
        setCurrentCode(code);
        setIsModalOpen(true);
    };

    const handleOpeningDeleteModal = (code: IAnalyticalCodes) => {
        setCurrentCode(code);
        setIsDeleteModalOpen(true);
    };

    const handleDelete = async () => {
        const response = await dispatch(deleteAnalyticalCode(currentCode));
        if (response.meta.requestStatus === "fulfilled") {
            setIsDeleteModalOpen(false);
        }
    };

    return (
        <div className="analytical-code-container">
            {/* Modals */}
            <ModalBox open={isModalOpen} onClose={() => setIsModalOpen(false)} mainTitle={t("AnalyticalCode.NewCode")}>
                <CodeForm
                    code={isEdit ? currentCode : undefined}
                    isEdit={isEdit}
                    companyId={company?.id}
                    close={() => {
                        setIsModalOpen(false);
                        isEdit && setIsEdit(false);
                    }}
                />
            </ModalBox>
            <ModalBox
                open={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                mainTitle={t("AnalyticalCode.DeleteTitle")}
            >
                <DeleteCode code={currentCode} close={() => setIsDeleteModalOpen(false)} />
            </ModalBox>
            <DeleteModal
                open={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onDelete={handleDelete}
                elementTitle={t("AnalyticalCode.DeleteElement", { codeName: currentCode?.name ?? "" })}
            />

            {/* Header */}
            <div className="codes-list-header">
                <h1 className="codes-list-title">{t("AnalyticalCode.Title")}</h1>
                <div className="codes-list-add-btn">
                    <ButtonBase
                        variant="text"
                        styles={{ ...buttonStyle, gap: "5px", backgroundColor: "#2D2D2D" }}
                        onClick={() => setIsModalOpen(true)}
                    >
                        <AddCircleOutline sx={{ fontSize: "1rem", fill: "white" }} />
                        {t("AnalyticalCode.AddCode")}
                    </ButtonBase>
                </div>
            </div>

            {/* List */}
            <div className="codes-list">
                {codesStatus === "pending" ? (
                    <div style={{ display: "flex", justifyContent: "center" }}>
                        <Spinner />
                    </div>
                ) : (
                    <TableContainer>
                        <Table>
                            {/* <TableHead>
                            <TableRow>
                                <TableCell align={"left"} sx={tHeadStyles}>{t("AnalyticalCode.Code")}</TableCell>
                            </TableRow>
                        </TableHead> */}
                            <TableBody>
                                {codes.map((code, i) => (
                                    <TableRow key={`code-${i}`} className="codes-table">
                                        <TableCell key={`code-name-${i}`}>{code.name}</TableCell>
                                        <TableCell key={`code-modify-${i}`} align="right">
                                            <Button
                                                className="code-actions edit"
                                                variant="text"
                                                onClick={() => handleClickEdit(code)}
                                            >
                                                {t("AnalyticalCode.Edit")}
                                            </Button>
                                            <Button
                                                className="code-actions delete"
                                                variant="text"
                                                onClick={() => handleOpeningDeleteModal(code)}
                                            >
                                                {t("AnalyticalCode.Delete")}
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </div>
        </div>
    );
};

export default AnalyticalCode;
